import React, { useEffect, useState } from 'react';
import { getAllQuestions } from '@/data/seoTopics';
import { motion } from 'framer-motion';
import { SEO } from '@/components/SEO';
import { Helmet } from 'react-helmet-async';
import { ChevronRight } from 'lucide-react';

export const QuestionPage = ({ slug, onNavigate }: { slug: string, onNavigate: (path: string) => void }) => {
  const [question, setQuestion] = useState<any>(null);

  useEffect(() => {
    const questions = getAllQuestions();
    const q = questions.find(x => x.slug === slug);
    if (q) setQuestion(q);
  }, [slug]);

  if (!question) return <div className="text-white p-12 text-center text-xl">Loading...</div>;

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "QAPage",
    "mainEntity": {
      "@type": "Question",
      "name": question.q,
      "text": question.q,
      "answerCount": 1,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": question.a,
        "author": {
          "@type": "Organization",
          "name": "ProfessorXAI"
        }
      }
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://professorxai.com/" },
      { "@type": "ListItem", "position": 2, "name": "Topics", "item": "https://professorxai.com/topics" },
      { "@type": "ListItem", "position": 3, "name": question.subject, "item": `https://professorxai.com/explore/${question.subject.toLowerCase()}` },
      { "@type": "ListItem", "position": 4, "name": question.topic, "item": `https://professorxai.com/explore/${question.subject.toLowerCase()}/${question.topic.toLowerCase()}` },
      { "@type": "ListItem", "position": 5, "name": question.q }
    ]
  };

  return (
    <>
      <SEO 
        title={`${question.q} | ${question.subject} | ProfessorXAI`}
        description={`Detailed step-by-step AI solution for: ${question.q}. Master ${question.topic} with ProfessorXAI's explanations.`}
        canonical={`https://professorxai.com/question/${slug}`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>
      <div className="max-w-5xl mx-auto px-6 py-12 text-slate-200 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Breadcrumbs */}
          <nav className="flex items-center space-x-2 text-sm text-slate-400 mb-8 overflow-x-auto whitespace-nowrap pb-2">
            <button onClick={() => onNavigate('/')} className="hover:text-white transition-colors">Home</button>
            <ChevronRight className="w-4 h-4 text-slate-600" />
            <button onClick={() => onNavigate('/topics')} className="hover:text-white transition-colors">Topics</button>
            <ChevronRight className="w-4 h-4 text-slate-600" />
            <button onClick={() => onNavigate(`/explore/${question.subject.toLowerCase().replace(/sl/g, '-')}`)} className="hover:text-white transition-colors">{question.subject}</button>
            <ChevronRight className="w-4 h-4 text-slate-600" />
            <span className="text-indigo-400">{question.topic}</span>
          </nav>
          
          <div className="bg-[#0a0a0f] border border-white/5 rounded-3xl p-8 md:p-12 shadow-2xl relative overflow-hidden">
            {/* Subtle background glow */}
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-500/10 rounded-full blur-[100px] pointer-events-none -translate-y-1/2 translate-x-1/2" />
            
            <div className="flex flex-wrap gap-3 items-center mb-6 relative z-10">
              <span className="px-3 py-1 rounded-full bg-slate-800/50 text-xs font-semibold text-slate-300 tracking-wider uppercase border border-slate-700">
                {question.subject}
              </span>
              <span className="px-3 py-1 rounded-full bg-slate-800/50 text-xs font-semibold text-slate-300 tracking-wider uppercase border border-slate-700">
                {question.topic}
              </span>
              <span className="px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-semibold tracking-wider uppercase border border-emerald-500/20">
                AI Verified Solution
              </span>
            </div>

            <h1 className="text-3xl md:text-5xl font-black text-white leading-tight mb-8 relative z-10">
              {question.q}
            </h1>

            <div className="prose prose-invert prose-lg max-w-none mb-12 relative z-10">
              <div className="p-6 md:p-8 rounded-2xl bg-slate-900/50 border border-white/5 shadow-inner">
                <h3 className="text-xl font-bold text-indigo-300 mb-4 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse" />
                  Step-by-Step AI Solution
                </h3>
                <p className="text-slate-200 leading-relaxed text-lg">
                  {question.a}
                </p>
              </div>
            </div>

            <div className="border-t border-slate-800/50 pt-8 mt-8 relative z-10">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full border border-slate-700 bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center p-0.5">
                    <img src="/profxlogo.png" alt="Professor X AI" className="w-full h-full rounded-full object-cover bg-black" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold">Professor X AI</h3>
                    <p className="text-slate-400 text-sm">Automated Cinematic Tutor</p>
                  </div>
                </div>
                <button 
                  onClick={() => onNavigate('/')}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 rounded-full font-bold shadow-[0_0_20px_rgba(79,70,229,0.3)] transition-all hover:scale-105"
                >
                  Generate Custom Video Answer
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
};
